import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "primereact/button";
import AppDialog from "../../Components/Dialogs/AppDialog/AppDialog";
import { DashlyTextInput, SelectableCardGroup } from "../../Components/inputs";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import { KnowledgeArticleSectionService, SECTION_TYPE, SectionType } from "../../Services/KnowledgeArticleSectionService";
import "./AddSectionDialog.scss";

interface AddSectionDialogProps {
    visible: boolean;
    /** Not needed when `onCreateLocal` is used — a brand-new article has no uuid yet. */
    knowledgeArticleUuid?: string;
    onHide: () => void;
    /** Section was created server-side; the parent adds it to its list (a new section always comes last).
     *  Ignored when `onCreateLocal` is set. */
    onCreated?: (sectionUuid: string, title: string, sectionType: SectionType) => void;
    /** When set, this dialog only picks title+type and hands them back — no network call. Used while
     *  composing a brand-new article, where every section is staged locally until the article's own Save
     *  creates the article and every section (with its media) in one go. */
    onCreateLocal?: (title: string, sectionType: SectionType) => void;
}

const AddSectionDialog: React.FC<AddSectionDialogProps> = ({ visible, knowledgeArticleUuid, onHide, onCreated, onCreateLocal }) => {
    const { t } = useTranslation();
    const knowledgeArticleSectionService = useMemo(() => new KnowledgeArticleSectionService(), []);
    const [title, setTitle] = useState("");
    const [sectionType, setSectionType] = useState<SectionType | "">("");
    const [saving, setSaving] = useState(false);

    const typeOptions: { value: SectionType | ""; title: string; description: string; icon: string }[] = [
        {
            value: SECTION_TYPE.IMAGE,
            title: t("knowledgeBase.form.sectionTypeImage") as string,
            description: t("knowledgeBase.form.sectionTypeImageDescription") as string,
            icon: "pi pi-image",
        },
        {
            value: SECTION_TYPE.VIDEO,
            title: t("knowledgeBase.form.sectionTypeVideo") as string,
            description: t("knowledgeBase.form.sectionTypeVideoDescription") as string,
            icon: "pi pi-video",
        },
        {
            value: SECTION_TYPE.SLIDESHOW,
            title: t("knowledgeBase.form.sectionTypeSlideshow") as string,
            description: t("knowledgeBase.form.sectionTypeSlideshowDescription") as string,
            icon: "pi pi-images",
        },
        {
            value: SECTION_TYPE.TEXT,
            title: t("knowledgeBase.form.sectionTypeText") as string,
            description: t("knowledgeBase.form.sectionTypeTextDescription") as string,
            icon: "pi pi-align-left",
        },
    ];

    const reset = () => {
        setTitle("");
        setSectionType("");
    };

    const handleHide = () => {
        reset();
        onHide();
    };

    const handleCreate = async () => {
        if (!title.trim() || !sectionType) {
            return;
        }
        if (onCreateLocal) {
            onCreateLocal(title.trim(), sectionType);
            reset();
            return;
        }
        if (!knowledgeArticleUuid) {
            return;
        }
        setSaving(true);
        try {
            const response = await knowledgeArticleSectionService.postItem({ knowledgeArticleUuid, title: title.trim(), sectionType });
            onCreated?.(response.data.data, title.trim(), sectionType);
            reset();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
        } finally {
            setSaving(false);
        }
    };

    return (
        <AppDialog
            visible={visible}
            onHide={handleHide}
            header={t("knowledgeBase.form.addSection") as string}
            style={{ width: "min(90vw, 560px)" }}
        >
            <div className="add-section-dialog">
                <DashlyTextInput
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("knowledgeBase.form.sectionTitlePlaceholder") as string}
                    required
                />
                <SelectableCardGroup options={typeOptions} value={sectionType} onChange={setSectionType} columns={2} />
                <div className="add-section-dialog__actions">
                    <Button type="button" label={t("common.action.cancel") as string} outlined onClick={handleHide} disabled={saving} />
                    <Button
                        type="button"
                        label={t("knowledgeBase.form.addSection") as string}
                        onClick={handleCreate}
                        loading={saving}
                        disabled={saving || !title.trim() || !sectionType}
                    />
                </div>
            </div>
        </AppDialog>
    );
};

export default AddSectionDialog;
